import React, { useState, useEffect } from 'react';
import { Link, Copy, RefreshCw, Send, QrCode, Check } from 'lucide-react';
import { useGroups } from '../../contexts/GroupsContext';
import { useTheme } from '../../contexts/ThemeContext';
import toast from 'react-hot-toast';

// ============================================================================
// INTERFACES
// ============================================================================

interface InviteManagerProps {
  groupJid: string;
  initialInviteCode?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const InviteManager: React.FC<InviteManagerProps> = ({ groupJid, initialInviteCode }) => {
  const { getInviteCode, revokeInviteCode, sendInvites, loading } = useGroups();
  const { theme } = useTheme();
  
  const [inviteCode, setInviteCode] = useState(initialInviteCode || '');
  const [inviteUrl, setInviteUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState('');
  const [customMessage, setCustomMessage] = useState('Você foi convidado para este grupo!');

  // Carregar código de convite se não foi fornecido
  useEffect(() => {
    if (!initialInviteCode) {
      loadInviteCode();
    } else {
      setInviteUrl(`https://chat.whatsapp.com/${initialInviteCode}`);
    }
  }, [initialInviteCode]);

  const loadInviteCode = async () => {
    try {
      const invite = await getInviteCode(groupJid);
      setInviteCode(invite.inviteCode);
      setInviteUrl(invite.inviteUrl);
    } catch (error) {
      console.error('Erro ao carregar código de convite:', error);
    }
  };

  // Copiar link para clipboard
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      toast.success('Link copiado para a área de transferência! 📋');
      
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      toast.error('Erro ao copiar link');
    }
  };

  // Revogar código atual
  const handleRevoke = async () => {
    if (!window.confirm('Tem certeza que deseja revogar o link atual? Um novo link será gerado e o antigo não funcionará mais.')) {
      return;
    }

    try {
      await revokeInviteCode(groupJid);
      await loadInviteCode(); // Recarregar novo código
    } catch (error) {
      console.error('Erro ao revogar convite:', error);
    }
  };

  // Enviar convites
  const handleSendInvites = async () => {
    // Validar números
    const numbers = phoneNumbers
      .split(/[\n,;]/)
      .map(n => n.trim())
      .filter(n => n.length > 0);

    if (numbers.length === 0) {
      toast.error('Digite pelo menos um número de telefone');
      return;
    }

    try {
      await sendInvites(groupJid, numbers, customMessage);
      setShowSendModal(false);
      setPhoneNumbers('');
      setCustomMessage('Você foi convidado para este grupo!');
    } catch (error) {
      console.error('Erro ao enviar convites:', error);
    }
  };

  // Abrir WhatsApp para compartilhar
  const shareViaWhatsApp = () => {
    const message = encodeURIComponent(`Junte-se ao nosso grupo no WhatsApp: ${inviteUrl}`);
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Link do convite */}
      <div>
        <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
          Link de Convite
        </label>
        
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: 'var(--text-muted)' }} />
            <input
              type="text"
              value={inviteUrl}
              readOnly
              className="w-full pl-10 pr-4 py-2.5 border rounded-lg font-mono text-sm font-normal"
              style={{
                backgroundColor: theme === 'dark' ? '#1a1a1b' : '#f9fafb',
                borderColor: 'var(--border-color)',
                color: 'var(--text-primary)'
              }}
            />
          </div>
          
          <button
            onClick={copyToClipboard}
            disabled={!inviteUrl}
            className={`
              px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2
              ${copied 
                ? 'bg-green-600 text-white' 
                : 'bg-blue-600 text-white hover:bg-blue-700'
              }
              disabled:opacity-50 disabled:cursor-not-allowed
            `}
          >
            {copied ? (
              <>
                <Check className="w-5 h-5" />
                <span className="hidden sm:inline">Copiado!</span>
              </>
            ) : (
              <>
                <Copy className="w-5 h-5" />
                <span className="hidden sm:inline">Copiar</span>
              </>
            )}
          </button>
        </div>

        {inviteCode && (
          <p className="mt-2 text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>
            Código: <span className="font-mono font-semibold">{inviteCode}</span>
          </p>
        )}
      </div>

      {/* QR Code (placeholder) */}
      <div 
        className="border-2 border-dashed rounded-lg p-8 text-center"
        style={{
          backgroundColor: theme === 'dark' ? '#1a1a1b' : '#f9fafb',
          borderColor: 'var(--border-color)'
        }}
      >
        <QrCode className="w-16 h-16 mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
        <p className="text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>
          QR Code para acesso rápido
        </p>
        <p className="text-xs font-normal mt-1" style={{ color: 'var(--text-muted)' }}>
          Escaneie para entrar no grupo
        </p>
      </div>

      {/* Ações */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <button
          onClick={shareViaWhatsApp}
          disabled={!inviteUrl}
          className="px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 text-sm font-medium"
        >
          <Send className="w-5 h-5" />
          Compartilhar
        </button>

        <button
          onClick={() => setShowSendModal(true)}
          disabled={!inviteUrl}
          className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 text-sm font-medium"
        >
          <Send className="w-5 h-5" />
          Enviar Convites
        </button>

        <button
          onClick={handleRevoke}
          disabled={loading || !inviteUrl}
          className="px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 text-sm font-medium"
        >
          <RefreshCw className="w-5 h-5" />
          Revogar Link
        </button>
      </div>

      {/* Modal de envio de convites */}
      {showSendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div 
            className="rounded-lg max-w-md w-full p-6"
            style={{ backgroundColor: 'var(--bg-card)' }}
          >
            <h3 className="text-xl font-semibold mb-4 text-n-slate-12">
              Enviar Convites por WhatsApp
            </h3>

            <div className="space-y-4">
              {/* Números de telefone */}
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                  Números de Telefone
                </label>
                <textarea
                  value={phoneNumbers}
                  onChange={(e) => setPhoneNumbers(e.target.value)}
                  placeholder="Digite os números (um por linha)&#10;Ex: 5511999999999&#10;     5511888888888"
                  rows={6}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm font-normal"
                  style={{
                    backgroundColor: 'var(--bg-card)',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)'
                  }}
                />
                <p className="mt-1 text-xs font-normal" style={{ color: 'var(--text-muted)' }}>
                  Separe os números por linha, vírgula ou ponto-e-vírgula
                </p>
              </div>

              {/* Mensagem personalizada */}
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                  Mensagem Personalizada
                </label>
                <textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm font-normal"
                  style={{
                    backgroundColor: 'var(--bg-card)',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>

              {/* Botões */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowSendModal(false)}
                  className={`flex-1 px-4 py-2 border rounded-lg transition-colors text-sm font-medium ${
                    theme === 'dark'
                      ? 'border-gray-600 hover:bg-gray-700'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                  style={{ color: 'var(--text-primary)' }}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSendInvites}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-medium"
                >
                  <Send className="w-4 h-4" />
                  Enviar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Informações */}
      <div 
        className={`border rounded-lg p-4 ${
          theme === 'dark'
            ? 'bg-blue-900 border-blue-700'
            : 'bg-blue-50 border-blue-200'
        }`}
      >
        <h4 className={`text-sm font-semibold mb-2 ${
          theme === 'dark' ? 'text-blue-200' : 'text-blue-900'
        }`}>
          ℹ️ Sobre o Link de Convite
        </h4>
        <ul className={`text-sm font-normal space-y-1 ${
          theme === 'dark' ? 'text-blue-300' : 'text-blue-800'
        }`}>
          <li>• Qualquer pessoa com o link pode entrar no grupo</li>
          <li>• Você pode revogar o link a qualquer momento</li>
          <li>• Ao revogar, um novo link será gerado automaticamente</li>
          <li>• Links antigos não funcionarão após a revogação</li>
        </ul>
      </div>
    </div>
  );
};

export default InviteManager;

